// §10 — what an app will accept, and whether a payload matches it.
//
// `accepts` is one list in a manifest (§9.2) and one field on an `InProcessApp` (§9.1), and the
// same question is asked of both by two different features: §10.2 highlights a drop target with
// it, and §10.3 builds the Share picker from it. So the matching lives here, in `shared`, where
// both the manifest schema and the app contract already are.
import type { Payload } from './api';

export interface AcceptRule {
  // Mime patterns. A trailing slash-star matches a whole type (`image/…`), and a bare star
  // matches anything. (Written out rather than shown, because the literal closes this comment.)
  mime: string[];
  action: 'open' | 'import';
}

/**
 * Does `mime` match `pattern`?
 *
 * Case-insensitive, and parameters are ignored — `text/plain; charset=utf-8` is `text/plain`,
 * because an app that accepts text should not have to enumerate encodings.
 */
export function mimeMatches(pattern: string, mime: string): boolean {
  const clean = (value: string): string => value.split(';')[0]?.trim().toLowerCase() ?? '';
  const want = clean(pattern);
  const got = clean(mime);
  if (want === '' || got === '') return false;
  if (want === '*/*' || want === '*') return true;
  if (want.endsWith('/*')) return got.startsWith(`${want.slice(0, -1)}`);
  return want === got;
}

/** Every accept rule that matches at least one item of `payload`. */
export function matchingRules(
  accepts: readonly AcceptRule[] | undefined,
  payload: Payload,
): AcceptRule[] {
  if (!accepts || accepts.length === 0) return [];
  return accepts.filter((rule) =>
    rule.mime.some((pattern) => payload.items.some((item) => mimeMatches(pattern, item.mime))),
  );
}

/**
 * Would this app take this payload at all?
 *
 * An app that declares no `accepts` accepts **nothing**. That is the safe default and the honest
 * one: §10.2 highlights only valid targets, and an app that never said it wanted content should
 * not light up as though it did.
 */
export function acceptsPayload(
  accepts: readonly AcceptRule[] | undefined,
  payload: Payload,
): boolean {
  return matchingRules(accepts, payload).length > 0;
}

/** The mime a filename implies, for a payload built from a path. Deliberately small. */
const EXTENSION_MIME: Record<string, string> = {
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.json': 'application/json',
  '.csv': 'text/csv',
  '.html': 'text/html',
  '.css': 'text/css',
  '.js': 'text/javascript',
  '.ts': 'text/plain',
  '.note': 'text/plain',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.pdf': 'application/pdf',
  '.zip': 'application/zip',
};

export function mimeForName(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return 'application/octet-stream';
  return EXTENSION_MIME[name.slice(dot).toLowerCase()] ?? 'application/octet-stream';
}
